import Modal, {ModalFooter} from '../shared/Modal.js';
import Button from '../shared/Button.js';
import React from 'react';
import {sb} from '../lib/supabase.js';
import {addDays, fmtDate, fmtMDY, toISO} from '../lib/dateUtils.js';

import {
  calcBreedingTimeline,
  buildCycleSeqMap,
  cycleLabel,
  resolveFarrowingSire,
  DEFAULT_BOAR_NAMES,
} from '../lib/pig.js';
import type {PigBreedingCycle, PigBreedingTimeline} from '../lib/pig.js';
import {programDotStyle} from '../lib/programColors.js';
import {expectedFrom, newPigRequestId, pigRejectionMessage} from '../lib/pigOperationsApi.js';
import {createRecordOpRunner, sameRecordValue} from './pigRecordOps.js';
import PigConflictNotice, {type PigConflictState} from './PigConflictNotice.js';
import {useAuth} from '../contexts/AuthContext.jsx';
import {useConfirm} from '../contexts/ConfirmContext.jsx';
import {usePig} from '../contexts/PigContext.jsx';
import {useAppDraftGuard, useAppLocation, useAppNavigate} from '../app/navigation/index.js';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import type {PigFarrowingCoreData} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import Header from '../shared/Header.tsx';
import InlineNotice from '../shared/InlineNotice.jsx';
import type {Notice} from '../shared/InlineNotice.js';
import StatusText from '../shared/StatusText.jsx';
import DataTable from '../shared/DataTable.jsx';
import {
  RecordPageFrame,
  RecordPageBody,
  RecordPageLoading,
  RecordPageNotFound,
  RecordBackLink,
  RecordTitle,
} from '../shared/RecordPageShell.jsx';
import FarrowingRecordForm, {farrowingLocationLabel, farrowingQualityLabel} from './FarrowingRecordForm.js';
import {farrowingFormFor} from './farrowingRecord.js';
import {farrowingRecordsForCycle, missedSowsForCycle} from './farrowingMisses.js';
import type {FarrowForm, FarrowingRecord} from './farrowingRecord.js';
import {createBreederDraftAutosave, type DraftSaveState} from './breederDraftAutosave.js';
import './FarrowingView.css';

const PIG_FARROWING_PARAMS = Object.freeze({});

// Farrowing records stay in ppp-farrowing-v1 but are written through
// pig_apply_operation (migration 235), which logs the pig.farrowing Activity
// and, when the sow is a known breeding pig, the pig.breeder one.
const FARROWING_ACTIVITY_LABELS = {
  sow: 'Sow',
  group: 'Group',
  farrowingDate: 'Farrowing Date',
  exposureStart: 'Exposure Start',
  sire: 'Sire',
  motheringQuality: 'Mothering Quality',
  demeanor: 'Demeanor',
  totalBorn: 'Total Born',
  deaths: 'Deaths',
  location: 'Location',
  wentWell: 'What Went Well',
  didntGoWell: "What Didn't Go Well",
  defects: 'Deaths / defects',
};

function recordIdAt(pathname: string) {
  if (!pathname.startsWith('/pig/farrowing/')) return null;
  try {
    return decodeURIComponent(pathname.slice('/pig/farrowing/'.length));
  } catch {
    return '';
  }
}
function count(value: unknown) {
  return parseInt(String(value)) || 0;
}
function statsFor(records: FarrowingRecord[]) {
  const born = records.reduce((sum, record) => sum + count(record.totalBorn), 0);
  const deaths = records.reduce((sum, record) => sum + count(record.deaths), 0);
  return {born, deaths, alive: born - deaths, survival: born ? Math.round(((born - deaths) / born) * 100) : 0};
}
function Metrics({records, total = false}: {records: FarrowingRecord[]; total?: boolean}) {
  const stats = statsFor(records);
  return (
    <div className="farrowing-metrics">
      {(total ? [{label: 'Total records', value: records.length}] : [])
        .concat([
          {label: 'Total Born', value: stats.born},
          {label: 'Deaths', value: stats.deaths},
          {label: 'Alive', value: stats.alive},
          {label: 'Survival', value: stats.survival},
        ])
        .map((metric) => (
          <div key={metric.label} className="farrowing-metric">
            <span>{metric.label}</span>
            <strong>
              {metric.value}
              {metric.label === 'Survival' ? '%' : ''}
            </strong>
          </div>
        ))}
    </div>
  );
}

export default function FarrowingView() {
  const {confirmDelete} = useConfirm();
  const {setSaveStatus} = useAuth();
  const navigate = useAppNavigate();
  const location = useAppLocation();
  const recordId = recordIdAt(location.pathname);
  const {
    farrowForm: farrowFormRaw,
    setFarrowForm: setFarrowFormRaw,
    showFarrowForm,
    setShowFarrowForm,
    setEditFarrowId,
  } = usePig();
  const farrowForm = farrowFormRaw as FarrowForm;
  const setFarrowForm = setFarrowFormRaw as unknown as React.Dispatch<React.SetStateAction<FarrowForm>>;
  const farrowingResource = useRouteResource(RESOURCE.PIG_FARROWING_CORE, PIG_FARROWING_PARAMS);
  const farrowingData = farrowingResource.data as PigFarrowingCoreData | null;
  const [savedRecs, setSavedRecs] = React.useState<FarrowingRecord[] | null>(null);
  const [notice, setNotice] = React.useState<Notice | null>(null);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [draft, setDraft] = React.useState<FarrowForm | null>(null);
  const draftRef = React.useRef(draft);
  draftRef.current = draft;
  const breedingCycles = (farrowingData?.breedingCycles || []) as PigBreedingCycle[];
  const farrowingRecs = savedRecs ?? ((farrowingData?.farrowingRecs || []) as FarrowingRecord[]);
  const boarNames = farrowingData?.boarNames ?? DEFAULT_BOAR_NAMES;
  const cycleSeqMap = buildCycleSeqMap(breedingCycles);
  const record = recordId !== null ? farrowingRecs.find((row) => String(row.id) === recordId) : null;
  const editing = !!record && editingId === recordId;
  const resolveSire = (row: FarrowingRecord) => resolveFarrowingSire(row, breedingCycles, boarNames);
  // Synchronous refs let a second edit/delete compose from the latest local
  // document even before React repaints. Serialize writes so a slower earlier
  // autosave cannot arrive after and overwrite a newer edit.
  const recordsRef = React.useRef(farrowingRecs);
  recordsRef.current = farrowingRecs;
  const addId = React.useRef<string | null>(null);
  const busy = React.useRef(false);
  const [saveState, setSaveState] = React.useState<DraftSaveState>('idle');
  // Farrowing records through pig_apply_operation (235). The server recomputes
  // `alive`, keeps the records newest first, logs the pig.farrowing Activity
  // (and the pig.breeder one when the sow is a known breeding pig), and --
  // under the same locks, in the same transaction -- creates the cycle's
  // farm-born feeder batch when this record is its first. The client no longer
  // writes ppp-feeders-v1 at all; a result naming a farm batch re-reads.
  const [applyFarrowOp] = React.useState(() =>
    createRecordOpRunner<FarrowingRecord>({
      sb,
      rowKey: 'farrowing',
      deletedKey: 'farrowing_id',
      getRows: () => recordsRef.current,
      setRows: (next) => {
        const sorted = [...next].sort((a, b) => (b.farrowingDate || '').localeCompare(a.farrowingDate || ''));
        recordsRef.current = sorted;
        setSavedRecs(sorted);
      },
      reread: () => void farrowingResourceRef.current.refetch(),
      onSaveStatus: setSaveStatus,
    }),
  );
  const farrowingResourceRef = React.useRef(farrowingResource);
  farrowingResourceRef.current = farrowingResource;
  const farrowRequest = React.useRef<{intent: string; requestId: string} | null>(null);
  const [farrowConflict, setFarrowConflict] = React.useState<PigConflictState | null>(null);
  async function persistForm(form: FarrowForm, id: string, create = false): Promise<boolean> {
    if (!form.sow.trim()) {
      setNotice({kind: 'error', message: 'Please enter a sow number.'});
      return false;
    }
    const existing = recordsRef.current.find((row) => String(row.id) === id) as Record<string, unknown> | undefined;
    // A removed record must never be recreated by a late autosave.
    if (!create && !existing) {
      setNotice({
        kind: 'error',
        message: 'This farrowing record is no longer available. Your edits have not been saved.',
      });
      return false;
    }
    const values = form as unknown as Record<string, unknown>;
    let request;
    if (!existing) {
      request = {operation: 'farrowing.create' as const, target: {farrowing_id: id}, change: {...values}};
    } else {
      const fields = Object.keys(values).filter((f) => !sameRecordValue(existing[f], values[f]));
      if (!fields.length) return true;
      request = {
        operation: 'farrowing.update' as const,
        target: {farrowing_id: id},
        expected: expectedFrom(existing, fields),
        change: Object.fromEntries(fields.map((f) => [f, values[f]])),
      };
    }
    const intent = JSON.stringify(request);
    const prior = farrowRequest.current;
    const requestId = prior && prior.intent === intent ? prior.requestId : newPigRequestId();
    farrowRequest.current = {intent, requestId};
    const outcome = await applyFarrowOp({requestId, ...request});
    const alreadyThere = !existing && outcome.status === 'rejected' && outcome.code === '23505';
    if (outcome.status === 'saved' || outcome.status === 'no_change' || alreadyThere) {
      farrowRequest.current = null;
      if (alreadyThere) void farrowingResourceRef.current.refetch();
      setFarrowConflict(null);
      setNotice(null);
      return true;
    }
    if (outcome.status === 'conflict') {
      farrowRequest.current = null;
      const conflicts = outcome.conflicts;
      setFarrowConflict({
        conflicts,
        labels: FARROWING_ACTIVITY_LABELS,
        useLatest: async () => {
          void farrowingResourceRef.current.refetch();
          setFarrowConflict(null);
          const base = draftRef.current ?? form;
          const merged = {...base} as Record<string, unknown>;
          for (const c of conflicts) merged[c.field] = c.current == null ? '' : c.current;
          setDraft(merged as unknown as FarrowForm);
          autosave.update({id, form: merged as unknown as FarrowForm});
        },
      });
      return false;
    }
    const why =
      outcome.status === 'rejected'
        ? pigRejectionMessage(outcome)
        : outcome.status === 'unknown'
          ? outcome.message
          : 'Retry the save.';
    setNotice({kind: 'error', message: 'Not Saved. Your answers are still here. ' + why});
    return false;
  }
  const persistRef = React.useRef(persistForm);
  persistRef.current = persistForm;
  const [autosave] = React.useState(() =>
    createBreederDraftAutosave<{id: string; form: FarrowForm; create?: boolean}>({
      // The controller never reaches persistForm for an invalid draft, so this
      // is where an edit learns why it is not saving (persistForm's own sow
      // check only guards a direct call). A confirmed save clears the notice.
      validate: ({form}) => {
        if (form.sow.trim()) return true;
        setNotice({kind: 'error', message: 'Please enter a sow number.'});
        return false;
      },
      persist: ({id, form, create}) => persistRef.current(form, id, create),
      onState: setSaveState,
    }),
  );
  React.useLayoutEffect(() => {
    autosave.resume();
    return () => autosave.suspend();
  }, [autosave]);
  const flush = React.useCallback(() => autosave.flush(), [autosave]);
  useAppDraftGuard({
    ownerKey: 'pig-farrowing-draft',
    isUnsafeToLeave: () => autosave.isDirty() || busy.current || showFarrowForm,
    settleBeforeLeave: async () => !busy.current && !showFarrowForm && (await flush()),
  });
  React.useEffect(() => {
    setEditingId(null);
  }, [recordId]);
  function updateDraft(form: FarrowForm) {
    setDraft(form);
    if (recordId !== null) autosave.update({id: recordId, form});
  }
  async function backToList() {
    if (busy.current || !(await flush())) return;
    setEditingId(null);
    setNotice(null);
    navigate('/pig/farrowing');
  }
  async function openRecord(row: FarrowingRecord) {
    if (busy.current || !(await flush())) return;
    setEditingId(null);
    setNotice(null);
    navigate('/pig/farrowing/' + encodeURIComponent(String(row.id)));
  }
  async function addRecord() {
    if (busy.current) return;
    if (!farrowForm.sow.trim()) {
      setNotice({kind: 'error', message: 'Please enter a sow number.'});
      return;
    }
    busy.current = true;
    addId.current ??= String(Date.now());
    autosave.update({id: addId.current, form: farrowForm, create: true});
    const confirmed = await flush();
    busy.current = false;
    if (!confirmed) return;
    addId.current = null;
    setShowFarrowForm(false);
    setEditFarrowId(null);
  }
  function startAdd(cycle?: PigBreedingCycle) {
    if (busy.current || autosave.isDirty()) return;
    addId.current = null;
    const timeline = cycle ? calcBreedingTimeline(cycle.exposureStart) : null;
    setFarrowForm({
      sow: '',
      group: String(cycle?.group || '1'),
      farrowingDate: '',
      exposureStart: cycle?.exposureStart || '',
      exposureEnd: timeline?.boarEnd || '',
      sire: boarNames.boar1,
      motheringQuality: '',
      demeanor: '',
      totalBorn: 0,
      deaths: 0,
      location: '',
      wentWell: '',
      didntGoWell: '',
      defects: '',
    });
    setNotice(null);
    setEditFarrowId(null);
    setShowFarrowForm(true);
  }
  function deleteRecord(row: FarrowingRecord) {
    if (busy.current) return;
    confirmDelete('Delete this farrowing record? This cannot be undone.', async () => {
      if (busy.current || !(await flush())) return;
      busy.current = true;
      const deleted = recordsRef.current.find((item) => item.id === row.id);
      if (!deleted) {
        busy.current = false;
        return;
      }
      const outcome = await applyFarrowOp({
        requestId: newPigRequestId(),
        operation: 'farrowing.delete',
        target: {farrowing_id: String(row.id)},
      });
      busy.current = false;
      if (outcome.status !== 'saved' && outcome.status !== 'no_change') {
        const why = outcome.status === 'rejected' ? pigRejectionMessage(outcome) : 'Please retry.';
        setNotice({kind: 'error', message: 'Not Deleted. The record is still here. ' + why});
        return;
      }
      await backToList();
    });
  }
  const getRecsForCycle = (cycle: PigBreedingCycle) => farrowingRecordsForCycle(cycle, farrowingRecs);
  const getMissedSows = (cycle: PigBreedingCycle, records: FarrowingRecord[], timeline: PigBreedingTimeline) =>
    missedSowsForCycle(cycle, records, timeline, cycleSeqMap);
  const sortedCycles = [...breedingCycles].sort((a, b) => (b.exposureStart || '').localeCompare(a.exposureStart || ''));
  const linkedIds = new Set(sortedCycles.flatMap((cycle) => getRecsForCycle(cycle).map((row) => row.id)));
  const unlinked = farrowingRecs
    .filter((row) => !linkedIds.has(row.id))
    .sort((a, b) => (b.farrowingDate || '').localeCompare(a.farrowingDate || ''));
  function RecordsTable({recs}: {recs: FarrowingRecord[]}) {
    return (
      <div className="farrowing-record-table">
        <DataTable<FarrowingRecord>
          rows={recs}
          rowKey="id"
          onRowOpen={openRecord}
          surfaceKey="pig-farrowing-records"
          rowProps={(row) => ({
            'data-farrow-record': row.id,
            'aria-label': `Open farrowing record for sow #${row.sow}`,
          })}
          emptyMessage="No farrowing records yet for this cycle"
          // Phone card (Table G): #Sow; farrow date and location; born, deaths
          // and alive. No badge until a farrowing threshold exists (D05).
          card={{
            main: (row) => '#' + row.sow,
            line1: (row) =>
              ['Farrowed ' + fmtDate(row.farrowingDate), farrowingLocationLabel(row.location)]
                .filter(Boolean)
                .join(' · '),
            line2: (row) => {
              const deaths = count(row.deaths);
              return (
                count(row.totalBorn) +
                ' born · ' +
                deaths +
                (deaths === 1 ? ' death' : ' deaths') +
                ' · ' +
                (count(row.totalBorn) - deaths) +
                ' alive'
              );
            },
          }}
          columns={[
            {key: 'sow', label: 'Sow', primary: true, render: (row) => <strong>#{row.sow}</strong>},
            {key: 'farrowingDate', label: 'Farrow date', render: (row) => fmtMDY(row.farrowingDate)},
            {
              key: 'impregnation',
              label: 'Impreg. date',
              render: (row) => (row.farrowingDate ? fmtMDY(toISO(addDays(row.farrowingDate, -116))) : '—'),
            },
            {key: 'sire', label: 'Sire', render: (row) => resolveSire(row) || '—'},
            {
              key: 'motheringQuality',
              label: 'Mothering',
              render: (row) => (
                <StatusText
                  tone={
                    row.motheringQuality === 'excellent'
                      ? 'ok'
                      : row.motheringQuality === 'poor'
                        ? 'danger'
                        : row.motheringQuality === 'average'
                          ? 'warn'
                          : 'muted'
                  }
                >
                  {farrowingQualityLabel(row.motheringQuality)}
                </StatusText>
              ),
            },
            {key: 'totalBorn', label: 'Born', align: 'right', render: (row) => count(row.totalBorn)},
            {key: 'deaths', label: 'Deaths', align: 'right', render: (row) => count(row.deaths)},
            {
              key: 'alive',
              label: 'Alive',
              align: 'right',
              render: (row) => <strong>{count(row.totalBorn) - count(row.deaths)}</strong>,
            },
            {key: 'location', label: 'Location', render: (row) => farrowingLocationLabel(row.location)},
            {
              key: 'notes',
              label: 'Notes',
              render: (row) => (
                <span title={[row.wentWell, row.didntGoWell, row.defects].filter(Boolean).join(' · ')}>
                  {[row.wentWell, row.didntGoWell, row.defects].filter(Boolean).join(' · ') || '—'}
                </span>
              ),
            },
          ]}
        />
      </div>
    );
  }
  const loadError = (
    <>
      <InlineNotice notice={{kind: 'error', message: 'Could not load farrowing records.'}} />
      <Button type="button" onClick={() => farrowingResource.refetch()}>
        Retry
      </Button>
    </>
  );
  if (recordId !== null) {
    if (farrowingResource.loading && !record)
      return <RecordPageLoading Header={Header} label="Loading farrowing record…" data-pig-farrowing-loading />;
    if (farrowingResource.error)
      return (
        <RecordPageFrame Header={Header}>
          <RecordPageBody>
            <RecordBackLink label="Back to Farrowing" onBack={backToList} />
            {loadError}
          </RecordPageBody>
        </RecordPageFrame>
      );
    if (!record)
      return (
        <RecordPageNotFound
          Header={Header}
          backLabel="Back to Farrowing"
          onBack={backToList}
          message="Farrowing record not found."
        />
      );
    const fields = [
      ['Sow', '#' + record.sow],
      ['Group', record.group ? 'Group ' + record.group : '—'],
      ['Farrowing Date', fmtMDY(record.farrowingDate)],
      ['Impregnation Date', record.farrowingDate ? fmtMDY(toISO(addDays(record.farrowingDate, -116))) : '—'],
      ['Exposure Start', fmtMDY(record.exposureStart)],
      ['Exposure End', fmtMDY(record.exposureEnd)],
      ['Sire', resolveSire(record) || '—'],
      ['Mothering Quality', farrowingQualityLabel(record.motheringQuality)],
      ['Location', farrowingLocationLabel(record.location)],
      ['Demeanor', record.demeanor || '—'],
    ];
    return (
      <RecordPageFrame Header={Header} className="farrowing-page" data-pig-farrowing-record-loaded="1">
        <RecordPageBody maxWidth={960}>
          <RecordBackLink label="Back to Farrowing" onBack={backToList} />
          <div className="farrowing-title-row">
            <div>
              <RecordTitle fontSize={28} margin="0" style={{fontWeight: 800}}>
                Farrowing · Sow #{record.sow}
              </RecordTitle>
              <p>{fmtMDY(record.farrowingDate)}</p>
            </div>
            <div className="farrowing-actions">
              {editing ? (
                <>
                  <span className="farrowing-autosave">Auto-saves as you type</span>
                  <Button
                    type="button"
                    onClick={async () => {
                      if (await flush()) {
                        setEditingId(null);
                        setNotice(null);
                      }
                    }}
                  >
                    Done
                  </Button>
                </>
              ) : (
                <Button
                  type="button"
                  onClick={() => {
                    setDraft(farrowingFormFor(record));
                    setEditingId(recordId);
                    setNotice(null);
                  }}
                >
                  Edit
                </Button>
              )}
              <Button variant="danger" type="button" onClick={() => deleteRecord(record)}>
                Delete
              </Button>
            </div>
          </div>
          <InlineNotice notice={notice} />
          <PigConflictNotice conflict={farrowConflict} />
          {(saveState === 'failed' || saveState === 'invalid') && (
            <Button onClick={() => void flush()}>Retry Save</Button>
          )}
          <section className="farrowing-card">
            <Metrics records={[record]} />
          </section>
          <section className="farrowing-card farrowing-detail">
            <h2>Farrowing Details</h2>
            {editing && draft ? (
              <FarrowingRecordForm
                form={draft}
                onChange={updateDraft}
                cycles={breedingCycles}
                records={farrowingRecs}
                recordId={recordId}
                resolveSire={resolveSire}
              />
            ) : (
              <>
                <dl className="farrowing-detail-grid">
                  {fields.map(([label, value]) => (
                    <div key={label}>
                      <dt>{label}</dt>
                      <dd>{value}</dd>
                    </div>
                  ))}
                </dl>
                <div className="farrowing-observations">
                  {[
                    ['What Went Well', record.wentWell],
                    ["What Didn't Go Well", record.didntGoWell],
                    ['Deaths / Defects', record.defects],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <h3>{label}</h3>
                      <p>{value || '—'}</p>
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>
        </RecordPageBody>
      </RecordPageFrame>
    );
  }
  return (
    <div className="farrowing-page">
      <Header />
      {showFarrowForm && (
        <Modal
          title="Add Farrowing Record"
          onClose={() => {
            if (busy.current || autosave.isDirty()) return;
            setShowFarrowForm(false);
            setNotice(null);
          }}
          footer={
            <ModalFooter
              primaryLabel="Add Record"
              primaryAttrs={{'data-farrow-save': '1'}}
              onPrimary={() => void addRecord()}
              primaryDisabled={saveState === 'saving'}
            />
          }
        >
          <div className="farrowing-detail">
            <InlineNotice notice={notice} onDismiss={() => setNotice(null)} />
            <PigConflictNotice conflict={farrowConflict} />
            <fieldset disabled={saveState === 'saving'} style={{border: 0, padding: 0, margin: 0}}>
              <FarrowingRecordForm
                form={farrowForm}
                onChange={(form) => {
                  if (!busy.current) setFarrowForm(form);
                }}
                cycles={breedingCycles}
                records={farrowingRecs}
                resolveSire={resolveSire}
              />
            </fieldset>
          </div>
        </Modal>
      )}
      <main className="farrowing-content">
        <div className="farrowing-title-row">
          <div>
            <h1>Farrowing</h1>
            <p>
              <strong>{farrowingRecs.length}</strong> records · <strong>{sortedCycles.length}</strong> breeding cycles
            </p>
          </div>
          <Button
            variant="primary"
            type="button"
            disabled={farrowingResource.loading || !!farrowingResource.error}
            onClick={() => startAdd()}
          >
            + Add Record
          </Button>
        </div>
        {farrowingResource.loading && !farrowingData ? (
          <div role="status" data-pig-farrowing-loading>
            Loading farrowing records…
          </div>
        ) : farrowingResource.error ? (
          <div data-pig-farrowing-load-error>{loadError}</div>
        ) : (
          <>
            <section className="farrowing-card">
              <Metrics records={farrowingRecs} total />
            </section>
            {sortedCycles.map((cycle) => {
              const timeline = calcBreedingTimeline(cycle.exposureStart);
              if (!timeline) return null;
              const records = getRecsForCycle(cycle);
              const missed = getMissedSows(cycle, records, timeline).filter((sow) => sow.status !== 'not_yet');
              const confirmed = missed.filter((sow) => sow.status === 'missed');
              const sowCount = count(cycle.sowCount);
              const percent = sowCount > 0 ? Math.min(100, Math.round((records.length / sowCount) * 100)) : 0;
              return (
                <section className="farrowing-card" key={cycle.id}>
                  <div className="farrowing-card-heading">
                    <h2>
                      <span style={programDotStyle('pig')} />
                      {cycleLabel(cycle, cycleSeqMap)}
                      <span className="farrowing-count">{records.length}</span>
                    </h2>
                    <Button type="button" data-farrow-add-record={cycle.id} onClick={() => startAdd(cycle)}>
                      + Add Record
                    </Button>
                  </div>
                  <div className="farrowing-cycle-meta">
                    <span>
                      Boars in{' '}
                      <strong>
                        {fmtMDY(cycle.exposureStart)} → {fmtMDY(timeline.boarEnd)}
                      </strong>
                    </span>
                    <span>
                      Farrowing window{' '}
                      <strong>
                        {fmtMDY(timeline.farrowingStart)} → {fmtMDY(timeline.farrowingEnd)}
                      </strong>
                    </span>
                    {cycle.boar1Tags && (
                      <span>
                        {cycle.boar1Name || boarNames.boar1 || 'Boar 1'} <strong>{cycle.boar1Tags}</strong>
                      </span>
                    )}
                    {cycle.boar2Tags && (
                      <span>
                        {cycle.boar2Name || boarNames.boar2 || 'Boar 2'} <strong>{cycle.boar2Tags}</strong>
                      </span>
                    )}
                  </div>
                  <div className="farrowing-progress">
                    <span>
                      Sows farrowed{' '}
                      <strong>
                        {records.length}
                        {sowCount > 0 ? ` of ${sowCount}` : ''}
                      </strong>
                    </span>
                    {sowCount > 0 && (
                      <>
                        <progress aria-label="Sows farrowed" max="100" value={percent} />
                        <span>{percent}%</span>
                      </>
                    )}
                  </div>
                  {!!records.length && <Metrics records={records} />}
                  <RecordsTable recs={records} />
                  {!!missed.length && (
                    <div className="farrowing-missed">
                      <h3>Sows not yet farrowed this cycle</h3>
                      <div>
                        {missed.map((sow) => (
                          <span key={sow.tag} className="farrowing-missed-sow">
                            <StatusText tone={sow.status === 'missed' ? 'danger' : 'warn'}>
                              #{sow.tag} — {sow.status}
                            </StatusText>
                          </span>
                        ))}
                      </div>
                      {!!confirmed.length && (
                        <p>
                          {confirmed.length} sow{confirmed.length > 1 ? 's' : ''} confirmed not pregnant this cycle —
                          farrowing window has passed
                        </p>
                      )}
                    </div>
                  )}
                </section>
              );
            })}
            {!!unlinked.length && (
              <section className="farrowing-card">
                <div className="farrowing-card-heading">
                  <div>
                    <h2>
                      Unlinked Records <span className="farrowing-count">{unlinked.length}</span>
                    </h2>
                    <p>These don't match any breeding cycle — edit to assign group/date</p>
                  </div>
                  <Button type="button" onClick={() => startAdd()}>
                    + Add Record
                  </Button>
                </div>
                <Metrics records={unlinked} />
                <RecordsTable recs={unlinked} />
              </section>
            )}
            {!farrowingRecs.length && (
              <div className="farrowing-empty">No farrowing records yet — use “+ Add Record” to record a litter.</div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
